import React, { useState, useEffect } from 'react';
import { useEmpresa } from '../../contexts/EmpresaContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  AlertTriangle, Building2, Users, Clock, FileText, 
  CheckCircle, XCircle, ChevronRight, Filter, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const EscritorioAlertas = () => {
  const { empresasDisponiveis, loading } = useEmpresa();
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const [alertas, setAlertas] = useState([]);
  const [loadingAlertas, setLoadingAlertas] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [filtroEmpresa, setFiltroEmpresa] = useState('todas');

  useEffect(() => {
    carregarAlertas();
  }, [empresasDisponiveis]);

  const carregarAlertas = async () => {
    if (!empresasDisponiveis || empresasDisponiveis.length === 0) {
      setLoadingAlertas(false);
      return;
    }

    try {
      setLoadingAlertas(true);
      const todosAlertas = [];

      for (const empresa of empresasDisponiveis) {
        try {
          // Buscar inconsistências pendentes
          const incResponse = await api.get(`/inconsistencias-ponto?empresa_id=${empresa.id}&status_filtro=pendente`);
          const inconsistencias = incResponse.data || [];
          
          inconsistencias.forEach(inc => {
            todosAlertas.push({
              id: inc.id,
              tipo: 'inconsistencia',
              titulo: inc.tipo?.replace(/_/g, ' ') || 'Inconsistência',
              descricao: inc.descricao || 'Pendência de ponto',
              empresa: empresa,
              empregado: inc.empregado_nome,
              data: inc.data,
              gravidade: inc.gravidade || 'media',
              created_at: inc.created_at
            });
          });

          // Buscar atestados pendentes
          const atestResponse = await api.get(`/atestados?empresa_id=${empresa.id}&status_filtro=pendente`);
          const atestados = atestResponse.data || [];
          
          atestados.forEach(atest => {
            todosAlertas.push({
              id: atest.id,
              tipo: 'atestado',
              titulo: 'Atestado Pendente',
              descricao: `${atest.dias_afastamento || 1} dia(s) de afastamento`,
              empresa: empresa,
              empregado: atest.empregado_nome,
              data: atest.data_inicio,
              gravidade: 'media',
              created_at: atest.created_at
            });
          });

        } catch (err) {
          console.error(`Erro ao carregar alertas de ${empresa.razao_social}:`, err);
        }
      }

      // Ordenar por data (mais recentes primeiro)
      todosAlertas.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setAlertas(todosAlertas);
    } catch (error) {
      toast.error('Erro ao carregar alertas');
      console.error(error);
    } finally {
      setLoadingAlertas(false);
    }
  };

  const formatData = (data) => {
    if (!data) return '-';
    const d = new Date(data);
    return d.toLocaleDateString('pt-BR');
  };

  const getGravidadeColor = (gravidade) => {
    switch (gravidade) {
      case 'alta':
      case 'grave':
        return 'destructive';
      case 'media':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getTipoIcon = (tipo) => {
    switch (tipo) {
      case 'inconsistencia':
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'atestado':
        return <FileText className="w-5 h-5 text-blue-500" />;
      default:
        return <Clock className="w-5 h-5 text-slate-500" />;
    }
  };

  const alertasFiltrados = alertas.filter(alerta => {
    if (filtroTipo !== 'todos' && alerta.tipo !== filtroTipo) return false;
    if (filtroEmpresa !== 'todas' && alerta.empresa.id !== filtroEmpresa) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="escritorio-alertas">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Alertas</h1>
          <p className="text-slate-600 mt-1">
            Pendências e inconsistências de todas as empresas
          </p>
        </div>
        <Button variant="outline" onClick={carregarAlertas} disabled={loadingAlertas}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loadingAlertas ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {alertas.filter(a => a.tipo === 'inconsistencia').length}
                </p>
                <p className="text-sm text-slate-500">Inconsistências</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {alertas.filter(a => a.tipo === 'atestado').length}
                </p>
                <p className="text-sm text-slate-500">Atestados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {new Set(alertas.map(a => a.empresa.id)).size}
                </p>
                <p className="text-sm text-slate-500">Empresas com alertas</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Tipo</label>
              <Select value={filtroTipo} onValueChange={setFiltroTipo}>
                <SelectTrigger data-testid="filtro-tipo">
                  <SelectValue placeholder="Todos os tipos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os tipos</SelectItem>
                  <SelectItem value="inconsistencia">Inconsistências</SelectItem>
                  <SelectItem value="atestado">Atestados</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Empresa</label>
              <Select value={filtroEmpresa} onValueChange={setFiltroEmpresa}>
                <SelectTrigger data-testid="filtro-empresa">
                  <SelectValue placeholder="Todas as empresas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas as empresas</SelectItem>
                  {empresasDisponiveis.map(empresa => (
                    <SelectItem key={empresa.id} value={empresa.id}>
                      {empresa.nome_fantasia || empresa.razao_social}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Alertas */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Alertas</CardTitle>
          <CardDescription>
            {alertasFiltrados.length} alerta{alertasFiltrados.length !== 1 ? 's' : ''} encontrado{alertasFiltrados.length !== 1 ? 's' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingAlertas ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : alertasFiltrados.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <CheckCircle className="w-12 h-12 text-green-500 mb-4" />
              <p className="text-lg font-medium">Nenhum alerta encontrado</p>
              <p className="text-sm">Todas as empresas estão em dia</p>
            </div>
          ) : (
            <div className="space-y-3">
              {alertasFiltrados.map((alerta) => (
                <div 
                  key={`${alerta.tipo}-${alerta.id}`}
                  className="flex items-start gap-4 p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <div className="flex-shrink-0 mt-1">
                    {getTipoIcon(alerta.tipo)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-slate-900 capitalize">
                        {alerta.titulo}
                      </h4>
                      <Badge variant={getGravidadeColor(alerta.gravidade)} className="text-xs">
                        {alerta.gravidade}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600 mb-2">{alerta.descricao}</p>
                    <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        {alerta.empresa.nome_fantasia || alerta.empresa.razao_social}
                      </span>
                      {alerta.empregado && (
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {alerta.empregado}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatData(alerta.data)}
                      </span>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => navigate(`/escritorio/empresas?empresa=${alerta.empresa.id}`)}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EscritorioAlertas;
